import { useEffect } from 'react';
import dayjs from 'dayjs';
import { useAllInvoicesStore, useDownloadInvoicesStore, usePrintInvoicesStore, useViewInvoicesStore } from '@lib/store';
import { cn, FormatUtils } from '@shared/utils';
import type { GetAllCardsResponse } from '@shared/api';
import { PdfViewer } from '@widgets';
import Download from '@assets/images/download.svg';
import Eye from '@assets/images/eye.svg';
import Print from '@assets/images/print.svg';
import DownloadMobile from '@assets/images/cards/invoices_download_ico.svg';
import EyeMobile from '@assets/images/cards/invoices_eye_ico.svg';
import PrintMobile from '@assets/images/cards/invoices_print_ico.svg';

interface TableInvoicesProps {
  className?: string;
  card: GetAllCardsResponse;
}

export const TableInvoices = (props: TableInvoicesProps) => {
  const allInvoicesStore = useAllInvoicesStore();
  const viewInvoicesStore = useViewInvoicesStore();
  const downloadInvoicesStore = useDownloadInvoicesStore();
  const printInvoicesStore = usePrintInvoicesStore();

  useEffect(() => {
    allInvoicesStore.getAllInvoicesCard(props.card.id);
  }, []);

  const viewHandler = (id: string) => {
    if (viewInvoicesStore.loading) return;
    viewInvoicesStore.viewInvoice(id);
  };
  const downloadHandler = (id: string, name: string) => {
    if (downloadInvoicesStore.loadingIds.has(id)) return;
    downloadInvoicesStore.downloadInvoice({ id, name });
  };
  const printHandler = (id: string) => {
    if (printInvoicesStore.loading) return;
    printInvoicesStore.printInvoice(id);
  };

  return (
    <div className={cn('m-0 px-4 py-0', props.className)}>
      <table className={cn('mt-15 border-collapse border-r border-r-[#979797]', 'max-md:w-full max-md:border-0')}>
        <thead className='max-md:hidden'>
          <tr className='border-r border-r-[#979797]'>
            <th className='border-r border-r-[#979797] bg-[#272727] px-10 py-5 text-center font-medium text-[#e5c496] uppercase'>date</th>
            <th className='w-87.25 border-r border-r-[#979797] bg-[#272727] px-10 py-5 text-center font-medium text-[#e5c496] uppercase'>invoice</th>
            <th className='w-80.25 border-r border-r-[#979797] bg-[#272727] px-10 py-5 text-center font-medium text-[#e5c496] uppercase'>amount</th>
            <th className='w-44.25 border-r border-r-[#979797] bg-[#272727] px-10 py-5 text-center font-medium text-[#e5c496] uppercase'>action</th>
          </tr>
        </thead>
        <tbody className='max-md:block max-md:w-full'>
          {!allInvoicesStore.data.length && (
            <tr
              className={cn(
                'w-full border-r border-b-2 border-r-[#979797] border-b-white bg-[#efefef] text-center',
                'max-md:relative max-md:mb-4 max-md:block max-md:h-auto max-md:rounded-lg max-md:border-0 max-md:bg-white max-md:p-4',
              )}
            >
              <td
                className={cn(
                  'h-[92.5px] border-r border-r-[#979797] px-5 py-1 text-center',
                  'max-md:flex max-md:h-45 max-md:w-full max-md:justify-center max-md:border-0 max-md:border-none max-md:bg-white max-md:px-0 max-md:py-1.5 max-md:text-left max-md:text-base max-md:leading-45 max-md:text-[#0f0f11]',
                )}
                colSpan={4}
              >
                Invoices were not found.
              </td>
            </tr>
          )}
          {allInvoicesStore.data.map((invoice, idx) => (
            <tr
              key={idx}
              className={cn(
                'border-r border-b-2 border-r-[#979797] border-b-white',
                'max-md:relative max-md:mb-4 max-md:block max-md:h-auto max-md:rounded-lg max-md:border-0 max-md:bg-white max-md:p-4',
                idx % 2 === 0 ? ['bg-[#efefef]', 'max-md:bg-white'] : ['bg-[#e6e6e6]', 'max-md:bg-white'],
              )}
            >
              <td
                className={cn(
                  'border-r border-r-[#979797] px-5 py-1 text-center',
                  'max-md:mb-6 max-md:flex max-md:h-auto max-md:w-full max-md:items-center max-md:justify-between max-md:border-none max-md:bg-white max-md:px-0 max-md:py-1.5 max-md:text-left max-md:text-base max-md:text-[#0f0f11]',
                )}
              >
                {dayjs(invoice.date).format('DD/MM/YY')}
              </td>
              <td
                className={cn(
                  'border-r border-r-[#979797] px-5 py-1 text-center',
                  'max-md:flex max-md:h-auto max-md:w-full max-md:justify-between max-md:border-none max-md:bg-white max-md:px-0 max-md:py-1.5 max-md:text-left max-md:text-base max-md:text-[#0f0f11]',
                )}
              >
                <div className={cn('hidden text-[#4c4c4c80]', 'max-md:block')}>Invoice</div>
                {invoice.name}
              </td>
              <td
                className={cn(
                  'border-r border-r-[#979797] px-5 py-1 text-center',
                  'max-md:flex max-md:h-auto max-md:w-full max-md:justify-between max-md:border-none max-md:bg-white max-md:px-0 max-md:py-1.5 max-md:text-left max-md:text-base max-md:text-[#0f0f11]',
                )}
              >
                <div className={cn('hidden text-[#4c4c4c80]', 'max-md:block')}>Amount</div>
                <div>
                  <span className='text-[#4c4c4c80]'>{invoice.currency}</span> {FormatUtils.insertSpaces(invoice.amount)}
                </div>
              </td>
              <td
                className={cn(
                  'flex items-center justify-center border-r border-r-[#979797] px-5 py-1 text-center',
                  'max-md:block max-md:h-auto max-md:w-full max-md:justify-between max-md:border-none max-md:bg-white max-md:p-0 max-md:py-1.5 max-md:text-left max-md:text-base max-md:text-[#0f0f11]',
                )}
              >
                <div className={cn('m-2 flex px-8', 'max-md:m-0 max-md:p-0 max-md:pt-4')}>
                  <div
                    className={cn(
                      'mx-3 flex cursor-pointer flex-col items-center justify-center pt-3 select-none',
                      'hover:underline',
                      'max-md:relative max-md:m-0 max-md:h-9 max-md:w-full max-md:flex-row max-md:gap-2 max-md:bg-[#fafafa] max-md:p-0 max-md:before:absolute max-md:before:top-1.25 max-md:before:bottom-1.25 max-md:before:left-0 max-md:before:w-px max-md:before:bg-[#4c4c4c10]',
                    )}
                    onClick={() => viewHandler(invoice.id)}
                  >
                    <i className='max-md:hidden'>
                      <Eye />
                    </i>
                    <i className={cn('hidden', 'max-md:inherit')}>
                      <EyeMobile />
                    </i>
                    <div className={cn('pt-3 text-[9px] text-[#4a4a4a]', 'max-md:p-0 max-md:text-base')}>View</div>
                  </div>
                  <div
                    className={cn(
                      'mx-3 flex cursor-pointer flex-col items-center justify-center pt-3 select-none',
                      'hover:underline',
                      'max-md:relative max-md:m-0 max-md:h-9 max-md:w-full max-md:flex-row max-md:gap-2 max-md:bg-[#fafafa] max-md:p-0 max-md:before:absolute max-md:before:top-1.25 max-md:before:bottom-1.25 max-md:before:left-0 max-md:before:w-px max-md:before:bg-[#4c4c4c10]',
                    )}
                    onClick={() => downloadHandler(invoice.id, `invoice_${invoice.name}`)}
                  >
                    <i className='max-md:hidden'>
                      <Download />
                    </i>
                    <i className={cn('hidden', 'max-md:inherit')}>
                      <DownloadMobile />
                    </i>
                    <div className={cn('pt-3 text-[9px] text-[#4a4a4a]', 'max-md:p-0 max-md:text-base')}>Download</div>
                  </div>
                  <div
                    className={cn(
                      'mx-3 flex cursor-pointer flex-col items-center justify-center pt-3 select-none',
                      'hover:underline',
                      'max-md:relative max-md:m-0 max-md:h-9 max-md:w-full max-md:flex-row max-md:gap-2 max-md:bg-[#fafafa] max-md:p-0 max-md:before:absolute max-md:before:top-1.25 max-md:before:bottom-1.25 max-md:before:left-0 max-md:before:w-px max-md:before:bg-[#4c4c4c10]',
                    )}
                    onClick={() => printHandler(invoice.id)}
                  >
                    <i className='max-md:hidden'>
                      <Print />
                    </i>
                    <i className={cn('hidden', 'max-md:inherit')}>
                      <PrintMobile />
                    </i>
                    <div className={cn('pt-3 text-[9px] text-[#4a4a4a]', 'max-md:p-0 max-md:text-base')}>Print</div>
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PdfViewer open={!!viewInvoicesStore.data} onClose={() => viewInvoicesStore.clear()} title='Invoices' data={viewInvoicesStore.data} />
    </div>
  );
};
